/*
  MONATEO — Suivi des ajouts au panier pour Klaviyo (« Added to Cart ») et Meta (« AddToCart »), côté thème.

  Pourquoi : les pixels Klaviyo et Meta installés par leurs apps Shopify ne tournent que si le
  visiteur a accepté la bannière de cookies (Customer Privacy API). Les visiteurs qui l'ignorent
  ou la refusent ne sont donc pas suivis. Ce module envoie l'ajout depuis le thème :
  - Klaviyo via klaviyo.js (le même script que « Viewed Product ») ;
  - Meta via le pixel Meta (fbevents.js), chargé seulement au premier ajout à envoyer.

  Principe : on observe les requêtes panier du navigateur (fetch + XMLHttpRequest), quel que
  soit le code qui les émet (fiche produit, quick add, tiroir, apps), plus le bouton d'achat
  express et les envois classiques de formulaire /cart/add. Une requête d'ajout réussie
  = un événement. Les requêtes d'origine ne sont jamais modifiées ni retardées.

  Configuration : window.MonateoCartTracking (posée par snippets/cart-tracking.liquid).
    klaviyoMode     'auto'   : n'envoie que si le pixel Klaviyo ne peut pas tourner (évite les doublons)
                    'always' : envoie toujours (désactiver alors le suivi panier du pixel dans Klaviyo)
                    'off'    : désactivé
    metaMode        mêmes valeurs, par rapport au pixel Meta de l'app Facebook & Instagram
    metaPixelId     identifiant du jeu de données / pixel Meta
    trackQuantity   suivre les augmentations de quantité (tiroir, page panier)
    trackExpress    suivre les clics sur le bouton d'achat express Shopify
    debug           journal dans la console
    snapshot        [{ key, q }] : état du panier au chargement de la page
*/
(function () {
  'use strict';

  var cfg = window.MonateoCartTracking || {};
  var klaviyoMode = cfg.klaviyoMode || 'auto';
  var metaMode = cfg.metaPixelId ? cfg.metaMode || 'auto' : 'off';
  if (window.__monateoCartTrackingInstalled || (klaviyoMode === 'off' && metaMode === 'off')) return;
  window.__monateoCartTrackingInstalled = true;

  var EVENT_NAME = 'Added to Cart';
  var META_EVENT_NAME = 'AddToCart';
  var QUEUE_KEY = 'monateo_cart_tracking_queue';
  var PENDING_FORM_KEY = 'monateo_kl_atc_pending_form';
  var QUEUE_MAX_AGE = 24 * 60 * 60 * 1000;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var origin = window.location.origin;

  var ADD_RE = /\/cart\/add(\.js)?\/?$/;
  var CHANGE_RE = /\/cart\/(change|update)(\.js)?\/?$/;

  function log() {
    if (!cfg.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Suivi panier]');
    console.info.apply(console, args);
  }

  function safe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        log('erreur ignorée', e);
      }
    };
  }

  /* ---------- Objet klaviyo (extrait officiel, sans effet si klaviyo.js est déjà chargé) ---------- */
  (function () {
    if (window.klaviyo || klaviyoMode === 'off') return;
    window._klOnsite = window._klOnsite || [];
    try {
      window.klaviyo = new Proxy({}, {
        get: function (n, i) {
          return i === 'push'
            ? function () { var n; (n = window._klOnsite).push.apply(n, arguments); }
            : function () {
                for (var n = arguments.length, o = new Array(n), w = 0; w < n; w++) o[w] = arguments[w];
                var t = typeof o[o.length - 1] === 'function' ? o.pop() : void 0;
                return new Promise(function (resolve) {
                  window._klOnsite.push([i].concat(o, [function (res) { t && t(res); resolve(res); }]));
                });
              };
        },
      });
    } catch (e) {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push = function () { var n; (n = window._klOnsite).push.apply(n, arguments); };
    }
  })();

  /* ---------- Doublons avec les pixels des apps (ils ne tournent qu'avec consentement) ---------- */
  function allowed(fnName) {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp[fnName] !== 'function') return false;
    try {
      return cp[fnName]() === true;
    } catch (e) {
      return false;
    }
  }

  // Pixel Klaviyo : finalités analyse + marketing. Pixel Meta : analyse + marketing + vente de données.
  function pixelCanRun(dest) {
    var base = allowed('analyticsProcessingAllowed') && allowed('marketingAllowed');
    if (dest === 'meta') return base && allowed('saleOfDataAllowed');
    return base;
  }

  function shouldTrack(dest) {
    var mode = dest === 'meta' ? metaMode : klaviyoMode;
    if (mode === 'off') return false;
    if (mode === 'always') return true;
    return !pixelCanRun(dest);
  }

  /* ---------- File d'envoi persistante (survit à une navigation immédiate) ---------- */
  function readQueue() {
    try {
      var q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      return Array.isArray(q) ? q : [];
    } catch (e) {
      return [];
    }
  }

  function writeQueue(q) {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    } catch (e) {
      /* stockage indisponible : l'envoi immédiat reste tenté */
    }
  }

  function removeFromQueue(id) {
    writeQueue(readQueue().filter(function (e) { return e.id !== id; }));
  }

  var inFlight = {};

  /* ---------- Pixel Meta, chargé à la demande ---------- */
  var metaReady = null;

  function loadMeta() {
    if (metaReady) return metaReady;
    metaReady = new Promise(function (resolve) {
      var existing = typeof window.fbq === 'function';
      if (!existing) {
        var n = (window.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        n.disablePushState = true; // pas de PageView automatique sur changement d'URL
      }
      try {
        window.fbq('set', 'autoConfig', false, cfg.metaPixelId); // pas de détection automatique de clics
        window.fbq('init', cfg.metaPixelId);
      } catch (e) {
        log('init Meta impossible', e);
      }
      if (existing) {
        resolve(true);
        return;
      }
      var script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.onload = function () { resolve(true); };
      script.onerror = function () { resolve(false); };
      (document.head || document.documentElement).appendChild(script);
    });
    return metaReady;
  }

  function metaParams(props) {
    var id = String(props.AddedItemVariantID || props.AddedItemProductID || '');
    var qty = props.AddedItemQuantity || 1;
    return {
      content_ids: [id],
      content_type: 'product',
      contents: [{ id: id, quantity: qty }],
      content_name: props.AddedItemProductName,
      value: Math.round(props.AddedItemPrice * qty * 100) / 100,
      currency: props.Currency || 'EUR',
    };
  }

  function sendMeta(entry) {
    loadMeta().then(function (ok) {
      if (!ok) {
        delete inFlight[entry.id];
        log('pixel Meta non chargé (bloqueur ?)', entry.props);
        return;
      }
      try {
        window.fbq('trackSingle', cfg.metaPixelId, META_EVENT_NAME, metaParams(entry.props), { eventID: entry.id });
        log('envoyé à Meta', metaParams(entry.props));
        setTimeout(function () { removeFromQueue(entry.id); }, 500);
      } catch (e) {
        delete inFlight[entry.id];
        log('échec envoi Meta', e);
      }
    });
  }

  function send(entry) {
    if (inFlight[entry.id]) return;
    inFlight[entry.id] = true;
    if (entry.dest === 'meta') {
      sendMeta(entry);
      return;
    }
    try {
      var result = window.klaviyo.track(EVENT_NAME, entry.props);
      log('envoyé à Klaviyo', entry.props);
      if (result && typeof result.then === 'function') {
        result.then(function () { removeFromQueue(entry.id); }, function () { delete inFlight[entry.id]; });
      } else {
        removeFromQueue(entry.id);
      }
    } catch (e) {
      delete inFlight[entry.id];
      log('échec envoi', e);
    }
  }

  function enqueue(props) {
    var base = Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    ['klaviyo', 'meta'].forEach(function (dest) {
      if (!shouldTrack(dest)) {
        log('ignoré pour ' + dest + ' (désactivé ou déjà couvert par le pixel de l\'app)', props);
        return;
      }
      var entry = { id: dest + '-' + base, dest: dest, ts: Date.now(), props: props };
      var q = readQueue();
      q.push(entry);
      writeQueue(q.slice(-40));
      send(entry);
    });
  }

  function flushQueue() {
    var now = Date.now();
    var q = readQueue().filter(function (e) {
      return now - e.ts < QUEUE_MAX_AGE && (e.dest === 'meta' ? metaMode !== 'off' : klaviyoMode !== 'off');
    });
    writeQueue(q);
    q.forEach(send);
  }

  /* ---------- Construction de l'événement (format Klaviyo pour Shopify) ---------- */
  function money(cents) {
    return Math.round(Number(cents || 0)) / 100;
  }

  function absUrl(u) {
    if (!u) return '';
    if (u.indexOf('//') === 0) return 'https:' + u;
    if (u.charAt(0) === '/') return origin + u;
    return u;
  }

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
  }

  function cartPermalink(cart) {
    if (!cart || !cart.items || !cart.items.length) return origin + root + 'cart';
    var parts = cart.items.map(function (i) { return i.variant_id + ':' + i.quantity; });
    return origin + root + 'cart/' + parts.join(',');
  }

  function itemOf(line) {
    return {
      ProductID: line.product_id,
      VariantID: line.variant_id,
      SKU: line.sku || '',
      ProductName: line.product_title,
      VariantName: line.variant_title || '',
      Quantity: line.quantity,
      ItemPrice: money(line.final_price != null ? line.final_price : line.price),
      RowTotal: money(line.final_line_price != null ? line.final_line_price : line.line_price),
      ProductURL: absUrl(line.url),
      ImageURL: absUrl(line.image || (line.featured_image && line.featured_image.url)),
      ProductCategories: line.product_type ? [line.product_type] : [],
      Brand: line.vendor || '',
    };
  }

  function buildProps(added, qty, cart, method) {
    var price = added.final_price != null ? added.final_price : added.price;
    var props = {
      AddedItemProductName: added.product_title,
      AddedItemProductID: added.product_id,
      AddedItemVariantID: added.variant_id || added.id,
      AddedItemVariantName: added.variant_title || '',
      AddedItemSKU: added.sku || '',
      AddedItemCategories: added.product_type ? [added.product_type] : [],
      AddedItemImageURL: absUrl(added.image || (added.featured_image && added.featured_image.url)),
      AddedItemURL: absUrl(added.url),
      AddedItemPrice: money(price),
      AddedItemQuantity: qty,
      AddMethod: method,
      TrackedBy: 'theme',
      Currency: currency(),
    };
    if (cart && cart.items) {
      props.$value = money(cart.total_price);
      props.ItemNames = cart.items.map(function (i) { return i.product_title; });
      props.Items = cart.items.map(itemOf);
      props.ItemCount = cart.item_count;
      props.CheckoutURL = cartPermalink(cart);
    } else {
      props.$value = money(price) * qty;
      props.ItemNames = [added.product_title];
      props.Items = [itemOf({
        product_id: added.product_id,
        variant_id: added.variant_id || added.id,
        sku: added.sku,
        product_title: added.product_title,
        variant_title: added.variant_title,
        quantity: qty,
        price: price,
        line_price: price * qty,
        url: added.url,
        image: added.image,
        product_type: added.product_type,
        vendor: added.vendor,
      })];
      props.ItemCount = qty;
      props.CheckoutURL = origin + root + 'cart/' + (added.variant_id || added.id) + ':' + qty;
    }
    return props;
  }

  /* ---------- Instantané du panier (pour détecter les hausses de quantité) ---------- */
  var snapshot = {};
  (cfg.snapshot || []).forEach(function (l) { snapshot[l.key] = l.q; });

  function takeSnapshot(cart) {
    if (!cart || !cart.items) return;
    snapshot = {};
    cart.items.forEach(function (i) { snapshot[i.key] = i.quantity; });
  }

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  function getCart() {
    if (!nativeFetch) return Promise.resolve(null);
    return nativeFetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ---------- Lecture des requêtes panier ---------- */
  function pathOf(url) {
    try {
      return new URL(url, origin).pathname;
    } catch (e) {
      return String(url || '');
    }
  }

  function requestedQuantities(body) {
    // Retourne { byVariant: {id: qty}, single: qty|null }
    var out = { byVariant: {}, single: null };
    if (!body) return out;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var id = body.get('id');
        var q = parseInt(body.get('quantity'), 10);
        if (id) out.byVariant[id] = isNaN(q) ? 1 : q;
        out.single = isNaN(q) ? 1 : q;
        return out;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        body = body.toString();
      }
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{') {
          var json = JSON.parse(trimmed);
          if (Array.isArray(json.items)) {
            json.items.forEach(function (it) { out.byVariant[it.id] = it.quantity || 1; });
          } else if (json.id) {
            out.byVariant[json.id] = json.quantity || 1;
            out.single = json.quantity || 1;
          }
        } else {
          var params = new URLSearchParams(trimmed);
          var pid = params.get('id');
          var pq = parseInt(params.get('quantity'), 10);
          if (pid) out.byVariant[pid] = isNaN(pq) ? 1 : pq;
          out.single = isNaN(pq) ? 1 : pq;
        }
      }
    } catch (e) {
      /* corps non lisible : quantité 1 par défaut */
    }
    return out;
  }

  var handleAdd = safe(function (responseJson, body) {
    if (!responseJson || responseJson.status) return; // erreur Shopify (rupture, etc.)
    var lines = Array.isArray(responseJson.items) ? responseJson.items : [responseJson];
    lines = lines.filter(function (l) { return l && (l.variant_id || l.id) && l.product_title; });
    if (!lines.length) return;
    var req = requestedQuantities(body);

    getCart().then(safe(function (cart) {
      lines.forEach(function (line) {
        var vid = line.variant_id || line.id;
        var qty = req.byVariant[vid] || req.single || 1;
        enqueue(buildProps(line, qty, cart, 'add'));
      });
      takeSnapshot(cart);
    }));
  });

  var handleChange = safe(function (cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    if (cfg.trackQuantity !== false) {
      cart.items.forEach(function (line) {
        if (!(line.key in snapshot)) return; // ligne inconnue : on ne devine pas
        var delta = line.quantity - snapshot[line.key];
        if (delta > 0) enqueue(buildProps(line, delta, cart, 'quantity_increase'));
      });
    }
    takeSnapshot(cart);
  });

  function route(url, method, body, parseJson) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return;
    var path = pathOf(url);
    if (ADD_RE.test(path)) {
      parseJson().then(function (json) { handleAdd(json, body); }, function () {});
    } else if (CHANGE_RE.test(path)) {
      parseJson().then(handleChange, function () {});
    }
  }

  /* ---------- Interception fetch ---------- */
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var promise = nativeFetch(input, init);
      try {
        var url = typeof input === 'string' ? input : input && input.url;
        var method = (init && init.method) || (input && input.method) || 'GET';
        var body = init && init.body;
        if (/\/cart\/(add|change|update)/.test(String(url))) {
          promise.then(function (response) {
            if (!response || !response.ok) return;
            route(url, method, body, function () { return response.clone().json(); });
          }, function () {});
        }
      } catch (e) {
        log('erreur ignorée (fetch)', e);
      }
      return promise;
    };
  }

  /* ---------- Interception XMLHttpRequest (apps, jQuery) ---------- */
  if (window.XMLHttpRequest) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__klMethod = method;
        this.__klUrl = url;
      } catch (e) {}
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        var xhr = this;
        if (/\/cart\/(add|change|update)/.test(String(xhr.__klUrl))) {
          xhr.addEventListener('load', function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            route(xhr.__klUrl, xhr.__klMethod, body, function () {
              return new Promise(function (resolve, reject) {
                try {
                  resolve(xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText));
                } catch (e) {
                  reject(e);
                }
              });
            });
          });
        }
      } catch (e) {}
      return xhrSend.apply(this, arguments);
    };
  }

  /* ---------- Envoi classique d'un formulaire /cart/add (sans AJAX) ---------- */
  window.addEventListener('submit', safe(function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form || !form.action || !ADD_RE.test(pathOf(form.action))) return;
    var data = new FormData(form);
    var id = data.get('id');
    if (!id) return;
    var q = parseInt(data.get('quantity'), 10);
    try {
      sessionStorage.setItem(PENDING_FORM_KEY, JSON.stringify({ id: String(id), q: isNaN(q) ? 1 : q, ts: Date.now() }));
    } catch (e) {}
  }));

  var resolvePendingForm = safe(function () {
    var raw;
    try {
      raw = sessionStorage.getItem(PENDING_FORM_KEY);
      sessionStorage.removeItem(PENDING_FORM_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var pending = JSON.parse(raw);
    if (!pending || Date.now() - pending.ts > 10 * 60 * 1000) return;
    getCart().then(safe(function (cart) {
      if (!cart || !cart.items) return;
      var line = cart.items.filter(function (i) { return String(i.variant_id) === pending.id; })[0];
      if (line) enqueue(buildProps(line, pending.q, cart, 'form_post'));
      takeSnapshot(cart);
    }));
  });

  /* ---------- Achat express (bouton de paiement dynamique Shopify) ---------- */
  function productJsonUrl(el) {
    var info = el.closest('product-info, [data-product-url], [data-url]');
    var url = info && (info.getAttribute('data-product-url') || info.getAttribute('data-url'));
    if (!url && /\/products\//.test(window.location.pathname)) url = window.location.pathname;
    if (!url) return null;
    return url.split('?')[0].replace(/\/$/, '') + '.js';
  }

  document.addEventListener('click', safe(function (event) {
    if (cfg.trackExpress === false) return;
    var button = event.target.closest && event.target.closest('.shopify-payment-button__button, .shopify-payment-button [role="button"]');
    if (!button) return;
    var form = button.closest('form');
    if (!form) return;
    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]') || document.querySelector('input[name="quantity"][form="' + form.id + '"]');
    var variantId = idInput && idInput.value;
    if (!variantId) return;
    var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
    var url = productJsonUrl(form);
    if (!url || !nativeFetch) return;
    nativeFetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(safe(function (product) {
        if (!product || !product.variants) return;
        var variant = product.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
        if (!variant) return;
        var image = (variant.featured_image && variant.featured_image.src) || product.featured_image;
        enqueue(buildProps({
          product_id: product.id,
          variant_id: variant.id,
          product_title: product.title,
          variant_title: variant.title === 'Default Title' ? '' : variant.title,
          sku: variant.sku,
          price: variant.price,
          url: product.url || root + 'products/' + product.handle,
          image: image,
          product_type: product.type,
          vendor: product.vendor,
        }, qty, null, 'express_checkout'));
      }))
      .catch(function () {});
  }), true);

  /* ---------- Démarrage ---------- */
  var start = safe(function () {
    flushQueue();
    resolvePendingForm();
    log('actif', {
      klaviyo: klaviyoMode,
      meta: metaMode,
      pixelKlaviyoPeutTourner: pixelCanRun('klaviyo'),
      pixelMetaPeutTourner: pixelCanRun('meta'),
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
